/**
 * Reconstruye el árbol de componentes a partir de la lista PLANA que devuelve el
 * API.
 *
 * El backend entrega los componentes sin anidar, cada uno con su `padre_id`. Los
 * componentes que renderizan el árbol (`NodoComponente`, `MostrarEstructura`)
 * necesitan la forma anidada, y esta es la única conversión.
 */

import type Componente from '@/interfaces/Componente'

/**
 * Convierte la lista plana en un bosque de nodos con `children`.
 *
 * Algoritmo en dos pasadas sobre un índice `Map` — **O(n)** en tiempo y O(n) en
 * espacio. La primera pasada indexa; la segunda enlaza cada nodo con su padre.
 * Hacerlo con búsquedas anidadas sería O(n²), que en un modelo con cientos de
 * aspectos se nota al pintar.
 *
 * NO muta la entrada: cada nodo se copia (`{ ...c, children: [] }`), de modo que
 * el array original del store queda intacto.
 *
 * ── Casos borde ──────────────────────────────────────────────────
 *
 *  - Lista vacía → `[]`.
 *  - **Huérfanos**: un nodo cuyo `padre_id` no está en la lista se DESCARTA en
 *    silencio, no se promueve a raíz. Es intencional para las vistas filtradas
 *    (una versión muestra solo parte del árbol), pero implica que un `padre_id`
 *    corrupto hace desaparecer el nodo sin error visible.
 *  - Varias raíces son válidas: el resultado es un bosque, no un único árbol.
 *  - Un ciclo en los datos produciría estructuras mutuamente referenciadas; esta
 *    función no lo detecta, porque el backend ya impide crearlos.
 *
 * @param lista - Componentes planos tal como llegan del API.
 * @returns Nodos raíz (`padre_id` nulo), cada uno con su descendencia anidada.
 *
 * @example
 * const arbol = construirArbolDeComponentes([
 *   { id_componente: 1, padre_id: null, nombre: 'Factor 1' },
 *   { id_componente: 2, padre_id: 1,    nombre: 'Característica 1.1' }
 * ])
 * // [{ id_componente: 1, children: [{ id_componente: 2, children: [] }] }]
 */
export function construirArbolDeComponentes(lista: Componente[]): Componente[] {
  const mapa = new Map<number, Componente>()
  const raices: Componente[] = []

  // Inicializar nodos con children vacíos
  lista.forEach(c => {
    mapa.set(c.id_componente!, { ...c, children: [] })
  })

  // Construir jerarquía con validación de padre_id
  lista.forEach(c => {
    const nodo = mapa.get(c.id_componente!)!
    if (c.padre_id != null) { 
      const padre = mapa.get(c.padre_id)
      if (padre) {
        padre.children!.push(nodo)
      }
    } else {
      raices.push(nodo)
    }
  })

  return raices
}